import "@/App.css";
import React, { createContext, useContext, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ReplayConsentBanner from "@/components/ReplayConsentBanner";

// ── Code splitting ───────────────────────────────────────────────────────────
// Every route is lazy so the initial download stays small. This matters most for
// the guest path (/i/:slug): guests open invitations from a WhatsApp link, often
// on mobile data, and previously had to fetch one ~2.2MB bundle containing the
// whole card editor, recharts and three.js before seeing anything. Route-level
// splitting keeps the host-only editor and dashboard chunks off that path
// entirely, and three.js is deferred again inside Invitation itself.
const Landing = lazy(() => import("@/pages/Landing"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CreateEvent = lazy(() => import("@/pages/CreateEvent"));
const EventManage = lazy(() => import("@/pages/EventManage"));
const EventAnalytics = lazy(() => import("@/pages/EventAnalytics"));
const Invitation = lazy(() => import("@/pages/Invitation"));
const Login = lazy(() => import("@/pages/Login"));
const EmailVerified = lazy(() => import("@/pages/EmailVerified"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const CardEditorPage = lazy(() => import("@/pages/CardEditorPage"));
const About = lazy(() => import("@/pages/About"));
const Templates = lazy(() => import("@/pages/Templates"));
const GuidesHub = lazy(() => import("@/pages/GuidesHub"));
const GuideArticlePage = lazy(() => import("@/pages/GuideArticlePage"));
const EventCategoryPage = lazy(() => import("@/pages/EventCategoryPage"));
const PaperlessPostAlternative = lazy(() => import("@/pages/PaperlessPostAlternative"));
const FreeInvitationMaker = lazy(() => import("@/pages/FreeInvitationMaker"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const AdobeExpressAlternative = lazy(() => import("@/pages/AdobeExpressAlternative"));
const ChungdoiAlternative = lazy(() => import("@/pages/ChungdoiAlternative"));
const CanvaAlternative = lazy(() => import("@/pages/CanvaAlternative"));
const ClickInvitationAlternative = lazy(() => import("@/pages/ClickInvitationAlternative"));
const WeddingInvitations = lazy(() => import("@/pages/WeddingInvitations"));
const IndianWeddingInvitations = lazy(() => import("@/pages/IndianWeddingInvitations"));
const BirthdayInvitations = lazy(() => import("@/pages/BirthdayInvitations"));
const BabyShowerInvitations = lazy(() => import("@/pages/BabyShowerInvitations"));
const WhatsAppInvitations = lazy(() => import("@/pages/WhatsAppInvitations"));
const SaveTheDateMaker = lazy(() => import("@/pages/SaveTheDateMaker"));
const QuinceaneraInvitations = lazy(() => import("@/pages/QuinceaneraInvitations"));
const DestinationWeddingInvitations = lazy(() => import("@/pages/DestinationWeddingInvitations"));
const ChineseWeddingInvitations = lazy(() => import("@/pages/ChineseWeddingInvitations"));
const DinnerPartyInvitations = lazy(() => import("@/pages/DinnerPartyInvitations"));
const CelebrationOfLifeInvitations = lazy(() => import("@/pages/CelebrationOfLifeInvitations"));

// ── Mobile App Mode Detection ────────────────────────────────────────────
// When loaded inside the mobile app's WebView, ?app=1 is appended to the URL.
// Components can use useAppMode() to conditionally hide marketing chrome.
const isInApp = new URLSearchParams(window.location.search).get("app") === "1";
const AppModeContext = createContext(isInApp);
export const useAppMode = () => useContext(AppModeContext);

/**
 * Neutral placeholder shown while a route chunk downloads. Deliberately plain:
 * a spinner that appears for 100ms reads as jank, and the invitation page sets
 * its own dark background immediately on mount.
 */
function RouteFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <span className="sr-only">Loading…</span>
    </div>
  );
}

function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Core & Landing */}
        <Route path="/" element={<Landing />} />
        <Route path="/about" element={<About />} />
        <Route path="/templates" element={<Templates />} />

        {/* Guides & Idea Engine */}
        <Route path="/guides" element={<GuidesHub />} />
        <Route path="/guides/:slug" element={<GuideArticlePage />} />

        {/* Category & Intent Landing Pages */}
        <Route path="/free-digital-invitation-maker" element={<FreeInvitationMaker />} />
        <Route path="/wedding-invitations" element={<WeddingInvitations />} />
        <Route path="/digital-wedding-invitations" element={<WeddingInvitations />} />
        <Route path="/indian-wedding-invitations" element={<IndianWeddingInvitations />} />
        <Route path="/quinceanera-invitations" element={<QuinceaneraInvitations />} />
        <Route path="/destination-wedding-invitations" element={<DestinationWeddingInvitations />} />
        <Route path="/chinese-wedding-invitations" element={<ChineseWeddingInvitations />} />
        <Route path="/dinner-party-invitations" element={<DinnerPartyInvitations />} />
        <Route path="/celebration-of-life-invitations" element={<CelebrationOfLifeInvitations />} />
        <Route path="/birthday-invitations" element={<BirthdayInvitations />} />
        <Route path="/digital-birthday-invitations" element={<BirthdayInvitations />} />
        <Route path="/baby-shower-invitations" element={<BabyShowerInvitations />} />
        <Route path="/whatsapp-invitations" element={<WhatsAppInvitations />} />
        <Route path="/whatsapp-wedding-invitations" element={<WhatsAppInvitations />} />
        <Route path="/save-the-date-maker" element={<SaveTheDateMaker />} />
        <Route path="/digital-save-the-date" element={<SaveTheDateMaker />} />

        {/* Programmatic Event Category Hubs */}
        <Route path="/housewarming-invitations" element={<EventCategoryPage categoryKey="housewarming-invitations" />} />
        <Route path="/engagement-invitations" element={<EventCategoryPage categoryKey="engagement-invitations" />} />
        <Route path="/anniversary-invitations" element={<EventCategoryPage categoryKey="anniversary-invitations" />} />
        <Route path="/bridal-shower-invitations" element={<EventCategoryPage categoryKey="bridal-shower-invitations" />} />
        <Route path="/naming-ceremony-invitations" element={<EventCategoryPage categoryKey="naming-ceremony-invitations" />} />
        <Route path="/corporate-event-invitations" element={<EventCategoryPage categoryKey="corporate-event-invitations" />} />
        <Route path="/reunion-invitations" element={<EventCategoryPage categoryKey="reunion-invitations" />} />
        <Route path="/graduation-invitations" element={<EventCategoryPage categoryKey="graduation-invitations" />} />

        {/* Competitor Comparison Pages */}
        <Route path="/paperless-post-alternative" element={<PaperlessPostAlternative />} />
        <Route path="/vs/paperless-post" element={<PaperlessPostAlternative />} />
        <Route path="/adobe-express-alternative" element={<AdobeExpressAlternative />} />
        <Route path="/vs/adobe-express" element={<AdobeExpressAlternative />} />
        <Route path="/chungdoi-alternative" element={<ChungdoiAlternative />} />
        <Route path="/vs/chungdoi" element={<ChungdoiAlternative />} />
        <Route path="/canva-alternative" element={<CanvaAlternative />} />
        <Route path="/vs/canva" element={<CanvaAlternative />} />
        <Route path="/click-invitation-alternative" element={<ClickInvitationAlternative />} />
        <Route path="/vs/click-invitation" element={<ClickInvitationAlternative />} />

        {/* Multilingual / Localized Route Support (/:lang/*) */}
        <Route path="/:lang/wedding-invitations" element={<WeddingInvitations />} />
        <Route path="/:lang/indian-wedding-invitations" element={<IndianWeddingInvitations />} />
        <Route path="/:lang/quinceanera-invitations" element={<QuinceaneraInvitations />} />
        <Route path="/:lang/destination-wedding-invitations" element={<DestinationWeddingInvitations />} />
        <Route path="/:lang/chinese-wedding-invitations" element={<ChineseWeddingInvitations />} />
        <Route path="/:lang/dinner-party-invitations" element={<DinnerPartyInvitations />} />
        <Route path="/:lang/celebration-of-life-invitations" element={<CelebrationOfLifeInvitations />} />
        <Route path="/:lang/birthday-invitations" element={<BirthdayInvitations />} />
        <Route path="/:lang/baby-shower-invitations" element={<BabyShowerInvitations />} />
        <Route path="/:lang/whatsapp-invitations" element={<WhatsAppInvitations />} />
        <Route path="/:lang/save-the-date-maker" element={<SaveTheDateMaker />} />
        <Route path="/:lang/free-digital-invitation-maker" element={<FreeInvitationMaker />} />
        <Route path="/:lang/paperless-post-alternative" element={<PaperlessPostAlternative />} />
        <Route path="/:lang/canva-alternative" element={<CanvaAlternative />} />
        <Route path="/:lang/adobe-express-alternative" element={<AdobeExpressAlternative />} />
        <Route path="/:lang/chungdoi-alternative" element={<ChungdoiAlternative />} />
        <Route path="/:lang/click-invitation-alternative" element={<ClickInvitationAlternative />} />
        <Route path="/:lang/housewarming-invitations" element={<EventCategoryPage categoryKey="housewarming-invitations" />} />
        <Route path="/:lang/engagement-invitations" element={<EventCategoryPage categoryKey="engagement-invitations" />} />
        <Route path="/:lang/anniversary-invitations" element={<EventCategoryPage categoryKey="anniversary-invitations" />} />
        <Route path="/:lang/bridal-shower-invitations" element={<EventCategoryPage categoryKey="bridal-shower-invitations" />} />
        <Route path="/:lang/naming-ceremony-invitations" element={<EventCategoryPage categoryKey="naming-ceremony-invitations" />} />
        <Route path="/:lang/corporate-event-invitations" element={<EventCategoryPage categoryKey="corporate-event-invitations" />} />
        <Route path="/:lang/reunion-invitations" element={<EventCategoryPage categoryKey="reunion-invitations" />} />
        <Route path="/:lang/graduation-invitations" element={<EventCategoryPage categoryKey="graduation-invitations" />} />
        <Route path="/:lang/templates" element={<Templates />} />
        <Route path="/:lang/about" element={<About />} />

        {/* Native Language Slug Aliases */}
        <Route path="/es/invitaciones-de-boda" element={<WeddingInvitations />} />
        <Route path="/es/invitaciones-de-quinceanera" element={<QuinceaneraInvitations />} />
        <Route path="/es/invitaciones-de-cumpleanos" element={<BirthdayInvitations />} />
        <Route path="/es/invitaciones-por-whatsapp" element={<WhatsAppInvitations />} />
        <Route path="/es/creador-de-invitaciones-gratis" element={<FreeInvitationMaker />} />
        <Route path="/zh/jiehun-qingjian" element={<ChineseWeddingInvitations />} />
        <Route path="/zh/dianzi-qingtie" element={<ChineseWeddingInvitations />} />
        <Route path="/zh/shengri-qingjian" element={<BirthdayInvitations />} />
        <Route path="/zh/mianfei-dianzi-qingjian" element={<FreeInvitationMaker />} />
        <Route path="/fr/faire-part-mariage" element={<WeddingInvitations />} />
        <Route path="/fr/invitation-anniversaire" element={<BirthdayInvitations />} />
        <Route path="/de/hochzeitseinladungen" element={<WeddingInvitations />} />
        <Route path="/de/geburtstagseinladungen" element={<BirthdayInvitations />} />
        <Route path="/hi/shadi-ka-card" element={<IndianWeddingInvitations />} />
        <Route path="/hi/vivah-nimantran" element={<IndianWeddingInvitations />} />

        {/* Legal & Auth */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/verified" element={<EmailVerified />} />
        {/* Completion step for the password recovery email. Both paths are routed
            because Supabase projects are commonly configured with either. */}
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected Host Application Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/create" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
        <Route path="/events/:eventId/edit" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
        <Route path="/events/:eventId" element={<ProtectedRoute><EventManage /></ProtectedRoute>} />
        <Route path="/events/:eventId/analytics" element={<ProtectedRoute><EventAnalytics /></ProtectedRoute>} />
        <Route path="/events/:eventId/card-editor" element={<ProtectedRoute><CardEditorPage /></ProtectedRoute>} />

        {/* Guest Invitation Routes */}
        <Route path="/i/:slug" element={<Invitation />} />
        <Route path="/i/:slug/g/:guestToken" element={<Invitation />} />
        {/* Shareable photo gallery. Same page, but it opens straight at the
            gallery instead of replaying the envelope — these links get sent
            after the event, when guests want the photos, not the invitation.
            Kept under /i/ so the OG proxy (which only matches ^/i/) still
            produces a link preview. */}
        <Route path="/i/:slug/photos" element={<Invitation focus="photos" />} />
        <Route path="/i/:slug/g/:guestToken/photos" element={<Invitation focus="photos" />} />
        {/* Shareable live stream. `focus` is the DOM id of the section to open at,
            which is why this is "live-stream" and not the "live_stream" section id. */}
        <Route path="/i/:slug/live" element={<Invitation focus="live-stream" />} />
        <Route path="/i/:slug/g/:guestToken/live" element={<Invitation focus="live-stream" />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <AppModeContext.Provider value={isInApp}>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <ReplayConsentBanner />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </BrowserRouter>
      </AppModeContext.Provider>
    </div>
  );
}

export default App;
